import React, { useRef, useEffect, useState } from "react";
import flatpickr from "flatpickr";
import { Select } from "../../../../common/Input/Input";
import { useBusinessContext } from "../../../../../context/business";
import { getDateString } from "../../../../../actions/getDateString";
import TotalCost from "../../TotalCost/TotalCost";
import numeral from "numeral";
import moment from "moment";

export default function DateForm({
  submitForm,
  currentStepIndex,
  useViewDetails,
}) {
  const flatpickr_date_picker = useRef();

  const {
    businessData: {
      businessData: { opening_week_days, unavailableDates, name: businessName },
      openingHours,
      availableSeats,
    },
    selectedBusiness,
    setSelectedBusiness,
  } = useBusinessContext();

  // Check if current booking is a group session
  const groupService = selectedBusiness.services?.find(s => s.is_group_session);
  const isGroupSession = !!groupService;
  const quantity = groupService?.quantity || 1;

  const [noAvailableSlot, setNoAvailableSlot] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBufferWarning, setShowBufferWarning] = useState(false);
  const [bufferWarningMessage, setBufferWarningMessage] = useState("");
  const [isUrgentWarning, setIsUrgentWarning] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const { setViewDetails } = useViewDetails();

  // Check if quantity exceeds available seats
  const quantityExceedsAvailable = isGroupSession &&
    selectedSlot &&
    selectedSlot.available_seats !== null &&
    quantity > selectedSlot.available_seats;

  function setDateTIm() {

     flatpickr(flatpickr_date_picker?.current, {
      disableMobile: true,
      enable: [
        (date) => {
          let openingWeekDays = opening_week_days?.includes(getDateString(date, false, true).day);
          let staff_opening_hours = selectedBusiness?.staff?.opening_hours;
          if (staff_opening_hours?.length > 0) {
            openingWeekDays = false;

            openingWeekDays = staff_opening_hours.some(hours => {
              return (hours.week_day.slice(0, 3) === getDateString(date, false, true).day);
            });
          }


          const disabledRanges = unavailableDates.map(item => {
            // Parse the start and end dates from the unavailableDates object
            const startDate = new Date(item.start_date);
            const endDate = new Date(item.end_date);

            // Return a range object for each unavailable date range
            return { start: startDate, end: endDate };
          });
          // Check if the date falls within disabled ranges
          const isInDisabledRange = disabledRanges.some(
            (range) => date >= range.start && date <= range.end
          );
          // Enable the date only if it is part of opening week days and not in disabled ranges
          return openingWeekDays && !isInDisabledRange;
        }],
       allowInput: true,
       defaultDate: null,
      minDate: (() => {
        const today = new Date();
        const minDate = new Date(today);

        if (!selectedBusiness?.totalCost?.business_settings?.same_day_booking) {
          minDate.setDate(today.getDate() + 1); // Next day if same-day booking is not allowed
        }
         minDate.setHours(0, 0, 0, 0);
        return minDate;
      })(),
      onChange: (dates, selectedDate) => {
        setSelectedBusiness({ type: "SELECT_DATE", data: selectedDate });
      },
    });
  }

  useEffect(() => {
    setDateTIm();
  }, [opening_week_days, currentStepIndex, selectedBusiness?.staff?.identifier, selectedBusiness?.totalCost?.business_settings?.same_day_booking]);

  useEffect(() => {
    setNoAvailableSlot(selectedBusiness.date && openingHours?.length < 1)
  }, [selectedBusiness, openingHours])

  // Helper function to convert seconds to human-readable format
  const formatBufferTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0 && minutes > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  };

  const handleOpeningHourChange = ({
    currentTarget: { value: selectedHour },
  }) => {
    setSelectedBusiness({
      type: "SELECT_HOUR",
      data: selectedHour,
    });
    // Find and set the selected slot for group sessions
    const slot = openingHours?.find(s => s.time === selectedHour);
    setSelectedSlot(slot || null);

    // Check for same-day booking buffer warning
    const isSameDayBooking = selectedBusiness?.totalCost?.business_settings?.same_day_booking;
    const bufferTimeInSeconds = selectedBusiness?.totalCost?.business_settings?.same_day_booking_time;

    if (isSameDayBooking && bufferTimeInSeconds && selectedHour && selectedBusiness.date) {
      const selectedDate = moment(selectedBusiness.date);
      const today = moment();

      // Check if the selected date is today
      const isToday = selectedDate.isSame(today, 'day');

      if (isToday) {
        // Parse the selected time slot (format: "HH:MM AM/PM")
        const appointmentTime = moment(selectedHour, "hh:mm A");
        const now = moment();

        // Calculate the deadline (appointment time - buffer time + 2 minutes grace period for payment)
        const paymentGracePeriod = 2 * 60; // 2 minutes in seconds
        const deadline = appointmentTime.clone().subtract(bufferTimeInSeconds + paymentGracePeriod, 'seconds');

        // Calculate time remaining until deadline
        const timeUntilDeadline = deadline.diff(now, 'seconds');

        // Show warning if we're within 10 minutes (600 seconds) of the deadline
        const warningThreshold = 10 * 60; // 10 minutes in seconds
        const urgentThreshold = 5 * 60; // 5 minutes in seconds

        if (timeUntilDeadline <= warningThreshold && timeUntilDeadline > 0) {
          const minutesRemaining = Math.floor(timeUntilDeadline / 60);
          const bufferFormatted = formatBufferTime(bufferTimeInSeconds);

          setShowBufferWarning(true);
          setIsUrgentWarning(timeUntilDeadline <= urgentThreshold);
          setIsDeadlinePassed(false);
          setBufferWarningMessage(
            `${businessName} requires ${bufferFormatted} notice for same-day appointments. You have approximately ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} to complete your booking, or this time slot may no longer be available.`
          );
        } else if (timeUntilDeadline <= 0) {
          // Deadline has passed - check if appointment time itself is in the past
          const timeUntilAppointment = appointmentTime.diff(now, 'seconds');

          setShowBufferWarning(true);
          setIsUrgentWarning(true);
          setIsDeadlinePassed(true);

          if (timeUntilAppointment <= 0) {
            // Appointment time is in the past
            setBufferWarningMessage(
              `This time slot has passed and is no longer available. Please select a later time or another date.`
            );
          } else {
            // Appointment time is in the future, but not enough buffer time
            setBufferWarningMessage(
              `This time slot is no longer available. ${businessName} requires ${formatBufferTime(bufferTimeInSeconds)} notice for same-day appointments. Please select a later time.`
            );
          }
        } else {
          setShowBufferWarning(false);
          setIsUrgentWarning(false);
          setIsDeadlinePassed(false);
          setBufferWarningMessage("");
        }
      } else {
        setShowBufferWarning(false);
        setIsUrgentWarning(false);
        setIsDeadlinePassed(false);
        setBufferWarningMessage("");
      }
    } else {
      setShowBufferWarning(false);
      setIsUrgentWarning(false);
      setIsDeadlinePassed(false);
      setBufferWarningMessage("");
    }
  };

  return (
    <form
      onSubmit={submitForm}
      className="step_container"
      data-is-active={(currentStepIndex === 3).toString()}
    >
      <TotalCost />
      <div className="date_time_select" required>
        <label className="default_input">
          <span className="label">DATE</span>
          <div className="input_rapper">
            <input autocomplete="off"
              type="text" required
              name="date"
              placeholder="Enter date here"
              className="input"
              ref={flatpickr_date_picker}
            />
          </div>
        </label>

        <Select
          labelText="TIME"
          id="booking-time"
          required
          onChange={handleOpeningHourChange}
          disabled={selectedBusiness.date && openingHours?.length === 0}
        >
          <option value="">
            {openingHours === null ? "Loading..." : "Select time"}
          </option>
          {openingHours?.map((slot, index) => (
            <option
              key={index}
              value={slot.time}
              selected={slot.time === selectedBusiness.hour}
            >
              {slot.time}
            </option>
          ))}
        </Select>
        {quantityExceedsAvailable && (
          <p className="quantity-exceeds-error">
            Your selected number of people ({quantity}) exceeds the available slots ({selectedSlot.available_seats}). Please reduce the number of people or select a different date or time.
          </p>
        )}
        {showBufferWarning && (
          <p className={`same-day-buffer-warning ${isUrgentWarning ? 'urgent' : ''}`}>
            {bufferWarningMessage}
          </p>
        )}
        { noAvailableSlot && (
          <p className="booking_time_input_message">
            No time slot long enough for this booking. Please select a different date.
          </p>
        )}
      </div>

      {selectedBusiness?.totalCost?.total_services_cost > 0 ? (
        <div className="footer_rapper">
          <div className="app_max_width flex">
            <div className="details">
              <p>{selectedBusiness?.services?.length || 0} Service(s)</p>
              <p className="flex">
                ₦{numeral(selectedBusiness?.totalCost?.total_services_cost).format('0,0.00')}
                {" "}
                <button
                  type="button"
                  onClick={() => setViewDetails(true)}
                  className="co_pink"
                >
                  View details
                </button>
              </p>
            </div>

            <button
              className={`round_button ${quantityExceedsAvailable || isDeadlinePassed ? 'disabled' : ''}`}
              disabled={quantityExceedsAvailable || isDeadlinePassed}
            >
              Continue
            </button>
          </div>
        </div>
      ) : null}
    </form>
  );
}
